import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, ShieldCheck, RefreshCw } from 'lucide-react';
import { RigaVerificaCosto, STATI_COSTO } from './RigaVerificaCosto';

const API = process.env.REACT_APP_BACKEND_URL;
const FILTRI = [['problemi', 'Da sistemare'], ['errore', null], ['dubbio', null], ['da_aggiornare', null], ['senza_fattura', null], ['ok', null], ['tutti', 'Tutti']];
const PROBLEMI = ['errore', 'dubbio', 'da_aggiornare'];

const Filtri = ({ dati, filtro, setFiltro }) => (
  <div className="flex flex-wrap gap-1.5" data-testid="verifica-costi-filtri">
    {FILTRI.map(([k, etichetta]) => {
      const n = k === 'tutti' ? dati.totale : k === 'problemi' ? PROBLEMI.reduce((s, p) => s + (dati.conteggi[p] || 0), 0) : dati.conteggi[k] || 0;
      return (
        <button key={k} onClick={() => setFiltro(k)} data-testid={`verifica-costi-filtro-${k}`}
          className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap border transition-colors ${filtro === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
          {etichetta || STATI_COSTO[k].testo} <span className="opacity-70">{n}</span>
        </button>
      );
    })}
  </div>
);

export const VerificaCostiDialog = ({ open, onClose, getAuthHeaders, onCorretto }) => {
  const [dati, setDati] = useState(null);
  const [filtro, setFiltro] = useState('problemi');
  const [correggo, setCorreggo] = useState(false);
  const [errore, setErrore] = useState(false);

  const carica = useCallback(async () => {
    setDati(null);
    setErrore(false);
    try {
      const r = await axios.get(`${API}/api/magazzino/vini/verifica-costi`, { headers: getAuthHeaders() });
      setDati(r.data);
      if (!PROBLEMI.some((p) => r.data.conteggi[p])) setFiltro('tutti');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verifica dei costi non riuscita');
      setErrore(true);
    }
  }, [getAuthHeaders]);

  useEffect(() => { if (open) { setFiltro('problemi'); carica(); } }, [open, carica]);

  const correggi = async () => {
    setCorreggo(true);
    try {
      const r = await axios.post(`${API}/api/magazzino/vini/verifica-costi/correggi`, {}, { headers: getAuthHeaders() });
      toast.success(`${r.data.corretti.length} costi corretti con l'ultima fattura`);
      onCorretto?.();
      await carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Correzione non riuscita');
    } finally {
      setCorreggo(false);
    }
  };

  if (!open) return null;
  const righe = !dati ? [] : dati.vini.filter((r) => filtro === 'tutti' || (filtro === 'problemi' ? PROBLEMI.includes(r.stato) : r.stato === filtro));
  const errori = dati?.conteggi.errore || 0;
  return (
    <div onClick={onClose} className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="verifica-costi-dialog">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-3xl max-h-[92vh] sm:rounded-2xl rounded-t-2xl flex flex-col shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-gray-900 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-700" /> Verifica costi della carta</h2>
            <p className="text-xs text-gray-500 mt-0.5" data-testid="verifica-costi-riepilogo">
              {dati ? `${dati.totale} vini confrontati con l'ultima fattura · ${errori} errori certi · ${dati.conteggi.dubbio || 0} da controllare` : 'Confronto ogni vino con la sua ultima fattura…'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="verifica-costi-chiudi"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-stone-50/60">
          {errore ? (
            <p className="py-12 text-center text-sm text-red-700" data-testid="verifica-costi-errore">Verifica non riuscita: premi il pulsante di ricontrollo.</p>
          ) : !dati ? (
            <p className="py-12 text-center text-sm text-gray-500 flex items-center justify-center gap-2" data-testid="verifica-costi-caricamento"><Loader2 className="h-5 w-5 animate-spin" /> Controllo le fatture…</p>
          ) : (
            <>
              <Filtri dati={dati} filtro={filtro} setFiltro={setFiltro} />
              {!righe.length && <p className="py-8 text-center text-sm text-emerald-700" data-testid="verifica-costi-vuoto">Nessun vino in questa sezione.</p>}
              {righe.map((r) => <RigaVerificaCosto key={r.id} r={r} />)}
            </>
          )}
        </div>
        <div className="px-5 pt-3 border-t flex flex-col sm:flex-row sm:items-center justify-between gap-2" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
          <p className="text-[11px] text-gray-500 sm:max-w-md">Correggendo, il costo torna quello dell'ultima fattura. I prezzi fissati a mano restano uguali, quelli automatici si ricalcolano.</p>
          <div className="flex gap-2 justify-end">
            <button onClick={carica} disabled={(!dati && !errore) || correggo} className="p-2 rounded-lg border text-gray-700 hover:bg-gray-50 disabled:opacity-50" title="Ricontrolla" data-testid="verifica-costi-ricarica"><RefreshCw className="h-4 w-4" /></button>
            <button onClick={correggi} disabled={!errori || correggo} data-testid="verifica-costi-correggi"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-red-700 text-white hover:bg-red-800 disabled:opacity-40">
              {correggo && <Loader2 className="h-4 w-4 animate-spin" />}
              {errori ? `Correggi ${errori} errori certi` : 'Nessun errore da correggere'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
